import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { fireEvent } from "@/lib/events";
import { useAuth } from "@/hooks/useAuth";
import { queryClient } from "@/lib/queryClient";

interface CreateSpaceSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Bottom sheet to create a brand-new owner space for the current user.
 * Calls the `create_owned_space` RPC which inserts the `spaces` row,
 * the matching `space_members` owner row, and flips
 * `profiles.active_space_id` to the new space — all in one transaction.
 *
 * Avatar upload is deferred — `spaces.avatar_url` doesn't exist yet.
 * The RPC accepts the parameter for a future-compatible signature.
 *
 * After success, invalidates TanStack Query caches and fires `space.switched`
 * so useAuth can rotate its own activeSpaceRole cache.
 */
const CreateSpaceSheet = ({ open, onOpenChange }: CreateSpaceSheetProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t("spaces.create.errorName"));
      return;
    }
    if (!user) return;

    setSubmitting(true);
    const { data: newSpaceId, error } = await supabase.rpc("create_owned_space", {
      p_name: trimmed,
      p_avatar_url: null,
    });
    setSubmitting(false);

    if (error || !newSpaceId) {
      toast.error(t("spaces.create.errorServer"));
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["active-space", user.id] });
    queryClient.invalidateQueries({ queryKey: ["spaces", user.id] });
    queryClient.invalidateQueries({ queryKey: ["current-role", user.id] });
    fireEvent("space.switched", { userId: user.id, spaceId: newSpaceId as string });

    toast.success(t("spaces.create.success", { name: trimmed }));
    onOpenChange(false);
    setName("");
    navigate("/", { replace: true });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>{t("spaces.create.sheetTitle")}</SheetTitle>
        </SheetHeader>
        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="create-space-name">
              {t("spaces.create.nameLabel")}
            </Label>
            <Input
              id="create-space-name"
              placeholder={t("spaces.create.namePlaceholder")}
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              className="h-12 text-base"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && !submitting) void handleSubmit();
              }}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-11"
              disabled={submitting}
            >
              {t("spaces.create.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 h-11"
              disabled={submitting || !name.trim()}
            >
              {submitting ? t("spaces.create.submitting") : t("spaces.create.submit")}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CreateSpaceSheet;
